import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Star, Sparkles, Info, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InsightsView } from "@/components/InsightsView";
import { PreviewDialog } from "@/components/PreviewDialog";
import { GuestTable } from "@/components/GuestTable";
import { CallOutDialog } from "@/components/CallOut";
import { GuestsTable } from "@/components/GuestsTable";
import { GuestFacts } from "@/components/GuestFacts";
import { MonthCalendar } from "@/components/MonthCalendar";
import { FutureRequests } from "@/components/FutureRequests";
import { BottomNav } from "@/components/BottomNav";
import { SideNav } from "@/components/SideNav";
import { QuickAddFab } from "@/components/QuickAddFab";
import { PullToRefresh } from "@/components/PullToRefresh";
import { HamburgerMenu } from "@/components/HamburgerMenu";
import { CallOutsTab } from "@/components/CallOutsTab";
import { HomeView } from "@/components/HomeView";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Settings } from "@/components/Settings";
import { AdminPage } from "@/components/AdminPage";
import { ScheduleReviewDialog } from "@/components/ScheduleReviewDialog";
import { NotificationNudge } from "@/components/NotificationNudge";
import { EmptyState } from "@/components/EmptyState";
import { FirstTimeTip } from "@/components/FirstTimeTip";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { tourTitle, withNewTourDefaults, parseTourDates, rangeBounds, isFutureRequest } from "@/lib/fields";
import { api } from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";
import { fuzzyMatch } from "@/lib/fuzzy";
import { SingleDatePicker } from "@/components/SingleDatePicker";
import { BrandLockup } from "@/components/BrandLockup";
import { RetireBanner, RetireDialog } from "@/components/RetireNotice";


const SAVED_VIEW_KEY = "toursSavedView";
const loadSavedView = () => {
  try { return JSON.parse(localStorage.getItem(SAVED_VIEW_KEY) || "null"); } catch { return null; }
};

// Module-level caches so returning from a detail page (Dashboard remount) paints the
// last-known tours/profiles instantly instead of flashing empty "welcome" states while refetching.
let guestsCache = null;
let profilesCache = null;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fabFileRef = useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [schedReview, setSchedReview] = useState({ open: false, changes: null, pending: null });
  const [schedBusy, setSchedBusy] = useState(false);
  const [guests, setGuests] = useState(() => guestsCache || []);
  const [guestsLoaded, setGuestsLoaded] = useState(guestsCache != null);
  const [profiles, setProfiles] = useState(() => profilesCache || []);
  const [profilesLoaded, setProfilesLoaded] = useState(profilesCache != null);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState(() => sessionStorage.getItem("activeTab") || "home");
  const savedView = loadSavedView();
  const [dateRange, setDateRange] = useState(savedView?.dateRange || "all");
  const [customFrom, setCustomFrom] = useState(savedView?.customFrom || "");
  const [customTo, setCustomTo] = useState(savedView?.customTo || "");
  const [pinnedView, setPinnedView] = useState(savedView);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFields, setPreviewFields] = useState({});
  const [previewProfile, setPreviewProfile] = useState({});
  const [previewReturn, setPreviewReturn] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [timeOffOpen, setTimeOffOpen] = useState(false);
  const [showPossible] = useState(false); // Future requests live on their own page now
  const [possTipDismissed, setPossTipDismissed] = useState(() => localStorage.getItem("sd_possible_tip") === "1");
  const dismissPossTip = () => { localStorage.setItem("sd_possible_tip", "1"); setPossTipDismissed(true); };

  // Admin: keep the pending-approval count fresh for the header badge.
  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    api.adminPendingCount().then((d) => active && setPendingCount(d.count || 0)).catch(() => {});
    return () => { active = false; };
  }, [user, tab]);

  const loadProfiles = useCallback(async () => {
    const p = await api.listProfiles();
    profilesCache = p;
    setProfiles(p);
    setProfilesLoaded(true);
  }, []);

  // On open, fetch only the tours (needed by Home/Tours/Calendar/Insights). Guest
  // profiles are heavier, so we defer them until the Guests tab is first opened.
  const refresh = useCallback(async () => {
    const g = await api.listGuests();
    guestsCache = g;
    setGuests(g);
    setGuestsLoaded(true);
    setProfilesLoaded((loaded) => {
      if (loaded) loadProfiles();
      return loaded;
    });
  }, [loadProfiles]);

  useEffect(() => { refresh(); }, [refresh]);

  // Lazy-load profiles the first time the Guests tab is opened.
  useEffect(() => {
    if (tab === "guests" && !profilesLoaded) loadProfiles();
  }, [tab, profilesLoaded, loadProfiles]);

  // Remember which tab you were on so returning from a detail page restores it.
  useEffect(() => { sessionStorage.setItem("activeTab", tab); }, [tab]);

  // Visiting the Calendar clears only the SCHEDULE pill (calendar updates are considered
  // reviewed once you land on the calendar). Tour pills stay until each is opened or dismissed.
  useEffect(() => { if (tab === "calendar") api.dismissImportEvents({ kind: "schedule" }).catch(() => {}); }, [tab]);

  // User-initiated tab change starts that tab at the top (scroll position per history
  // entry is handled globally by ScrollManager in App.js).
  const [focusExport, setFocusExport] = useState(() => (sessionStorage.getItem("sd_focus_export") ? 1 : 0));
  useEffect(() => { sessionStorage.removeItem("sd_focus_export"); }, []);
  const changeTab = useCallback((t) => {
    setTab(t);
    setFocusExport(0);
    window.scrollTo(0, 0);
  }, []);
  const showExport = useCallback(() => { setTab("settings"); setFocusExport((n) => n + 1); }, []);

  // After a call-out import (from Settings), jump to Call Outs so the updated FMLA balance is visible.
  useEffect(() => {
    const h = () => { setTab("callouts"); window.scrollTo(0, 0); };
    window.addEventListener("sd:goto-callouts", h);
    return () => window.removeEventListener("sd:goto-callouts", h);
  }, []);

  // Open the Calendar tab focused on a specific date (from the Home "Next Shift" card or global search).
  const [calendarFocus, setCalendarFocus] = useState(null);
  const openCalendarDate = useCallback((dateStr) => {
    const m = String(dateStr || "").match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    let d = null;
    if (m) { const y = m[3].length === 2 ? "20" + m[3] : m[3]; d = new Date(+y, +m[1] - 1, +m[2]); }
    setCalendarFocus(d && !isNaN(d.getTime()) ? d : new Date());
    changeTab("calendar");
  }, [changeTab]);

  // Global search "Shift notes" results open the calendar on that date. Works whether
  // the search is fired from the dashboard (event) or another route (pending date on mount).
  useEffect(() => {
    const h = (e) => { sessionStorage.removeItem("sd:pendingCalendarDate"); openCalendarDate(e?.detail); };
    window.addEventListener("sd:open-calendar-date", h);
    return () => window.removeEventListener("sd:open-calendar-date", h);
  }, [openCalendarDate]);

  useEffect(() => {
    const pd = sessionStorage.getItem("sd:pendingCalendarDate");
    if (pd) { sessionStorage.removeItem("sd:pendingCalendarDate"); openCalendarDate(pd); }
  }, [openCalendarDate]);

  // Long-press shortcuts from the bottom nav (also arriving via sessionStorage from other pages).
  const applyNavIntent = useCallback((intent) => {
    if (intent === "calendar-today") {
      setCalendarFocus(new Date());
      changeTab("calendar");
      toast.success("Jumped to today");
    } else if (intent === "tours-week") {
      setDateRange("week");
      changeTab("tours");
      toast.success("Showing this week's tours");
    } else if (intent === "home-refresh") {
      changeTab("home");
      window.scrollTo(0, 0);
      refresh();
      toast.success("Refreshed");
    }
  }, [changeTab, refresh]);

  const handleNavLongPress = useCallback((key) => {
    if (key === "calendar") applyNavIntent("calendar-today");
    else if (key === "tours") applyNavIntent("tours-week");
    else if (key === "home") applyNavIntent("home-refresh");
    else changeTab(key);
  }, [applyNavIntent, changeTab]);

  useEffect(() => {
    const intent = sessionStorage.getItem("sd:navIntent");
    if (intent) { sessionStorage.removeItem("sd:navIntent"); applyNavIntent(intent); }
  }, [applyNavIntent]);

  // Quick-Add FAB actions.
  const onFabAddShift = useCallback(() => {
    if (tab === "calendar") window.dispatchEvent(new Event("sd:new-shift"));
    else { sessionStorage.setItem("sd:openNewShift", "1"); changeTab("calendar"); }
  }, [tab, changeTab]);

  // Reload data for the current tab (used by pull-to-refresh).
  const refreshForTab = useCallback(async () => {
    if (tab === "calendar") window.dispatchEvent(new Event("schedule-invalidate"));
    await refresh();
  }, [tab, refresh]);

  // After an import/upload, quietly check whether the just-added guest looks like a
  // duplicate of an existing one, and nudge toward the manual cleanup in Settings.
  const checkNewDuplicate = useCallback(async (name) => {
    if (!name) return;
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    try {
      const groups = await api.duplicateProfiles();
      const hit = groups.some((g) => g.members.some((m) => norm(m.name) === norm(name)));
      if (hit) {
        toast.warning("Possible duplicate guest", {
          description: "Review it under Settings → Data cleanup.",
          duration: 8000,
        });
      }
    } catch (e) { console.warn("duplicate-guest check failed:", e); }
  }, []);

  const handleSave = async (fields, profile) => {
    try {
      const created = await api.createGuest(fields, profile);
      setPreviewOpen(false);
      toast.success("Tour saved to your database");
      refresh();
      checkNewDuplicate(fields?.guest_name);
      if (created?.id) navigate(`/guest/${created.id}`); // open the new tour
    } catch {
      toast.error("Could not save tour");
    }
  };

  // Single smart upload: auto-detect a tour sheet vs a work schedule and route accordingly.
  const handleSmartUpload = async (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp)/i.test(file.type)) { toast.error("Upload a PNG, JPG or WEBP screenshot"); return; }
    const dataUrl = await fileToResizedDataUrl(file, 1568, 0.82);
    const tid = toast.loading("Reading your screenshot…");
    try {
      const { shifts: found, is_schedule } = await api.extractSchedule(dataUrl);
      const validShifts = (found || []).filter((s) => s.date && String(s.date).trim());
      if (is_schedule && validShifts.length) {
        const res = await api.previewSchedule(validShifts);
        toast.dismiss(tid);
        setSchedReview({ open: true, changes: res.changes, pending: validShifts });
      } else {
        const data = await api.extract(dataUrl);
        toast.success("Tour sheet read — review & save", { id: tid });
        navigate("/guest/new", { state: { fields: { ...(data.fields || {}), tour_sheet_image: dataUrl }, profile: data.profile || {}, return_match: data.return_match || null } });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read that screenshot", { id: tid });
    }
  };

  const applyPendingSchedule = async (picks, delIds) => {
    if (!schedReview.pending) { setSchedReview((s) => ({ ...s, open: false })); return; }
    setSchedBusy(true);
    try {
      const res = await api.applySchedule(schedReview.pending, picks, delIds);
      refresh();
      const bits = [];
      if (res.added) bits.push(`${res.added} added`);
      if (res.changed) bits.push(`${res.changed} changed`);
      if (res.deleted) bits.push(`${res.deleted} deleted`);
      setSchedReview((s) => ({ ...s, open: false }));
      toast.success(bits.length ? `Schedule imported — ${bits.join(", ")}` : "Schedule already up to date", {
        duration: 10000,
        action: { label: "Undo", onClick: async () => { await api.undoSchedule(); refresh(); } },
      });
      changeTab("calendar");
    } catch {
      toast.error("Import failed");
    } finally { setSchedBusy(false); }
  };

  const convertToScheduled = async (g) => {
    try {
      await api.updateGuest(g.id, { status_override: "Future (Scheduled)" }, {});
      refresh();
      toast.success("Tour confirmed — moved to your schedule", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.updateGuest(g.id, { status_override: "Future (Request)" }, {}); toast.success("Moved back to Future Request"); refresh(); }
            catch { toast.error("Couldn't undo"); }
          },
        },
      });
    } catch {
      toast.error("Couldn't convert tour");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const t = deleteTarget;
    try {
      await api.deleteGuest(t.id);
      setDeleteTarget(null);
      refresh();
      toast.success("Tour moved to Trash", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try { await api.restoreGuest(t.id); toast.success("Tour restored"); refresh(); }
            catch { toast.error("Couldn't restore tour"); }
          },
        },
      });
    } catch {
      toast.error("Delete failed");
    }
  };

  const bounds = rangeBounds(dateRange, customFrom, customTo);

  const isPinned = !!pinnedView &&
    pinnedView.dateRange === dateRange &&
    (pinnedView.customFrom || "") === customFrom &&
    (pinnedView.customTo || "") === customTo;

  const togglePin = () => {
    if (isPinned) {
      localStorage.removeItem(SAVED_VIEW_KEY);
      setPinnedView(null);
      toast.success("Default view cleared");
    } else {
      const v = { dateRange, customFrom, customTo };
      localStorage.setItem(SAVED_VIEW_KEY, JSON.stringify(v));
      setPinnedView(v);
      toast.success("Pinned — Tours opens with this filter");
    }
  };

  const isPossible = (g) => isFutureRequest(g);
  const possibleCount = guests.filter(isPossible).length;
  const filtered = guests.filter((g) => {
    // Segregate "Future (Possible)" (rebooking wishlist): default view hides them; the toggle shows only them.
    if (showPossible ? !isPossible(g) : isPossible(g)) return false;
    if (query) {
      const hay = `${tourTitle(g)} ${g.guest_name || ""} ${g.tour_type || ""}`;
      if (!fuzzyMatch(hay, query)) return false;
    }
    if (bounds && !showPossible) {
      const [from, to] = bounds;
      const dates = parseTourDates(g.tour_date);
      if (!dates.length) return false;
      const hit = dates.some((d) => (!from || d >= from) && (!to || d <= to));
      if (!hit) return false;
    }
    return true;
  }).sort((a, b) => {
    const da = parseTourDates(a.tour_date);
    const dbb = parseTourDates(b.tour_date);
    if (showPossible) {
      // Possible tours: soonest date closest to today at the top (undated go last).
      const now = Date.now();
      const na = da.length ? Math.min(...da.map((x) => Math.abs(x.getTime() - now))) : Infinity;
      const nb = dbb.length ? Math.min(...dbb.map((x) => Math.abs(x.getTime() - now))) : Infinity;
      if (na !== nb) return na - nb;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    }
    const ma = da.length ? Math.max(...da.map((x) => x.getTime())) : -Infinity;
    const mb = dbb.length ? Math.max(...dbb.map((x) => x.getTime())) : -Infinity;
    if (mb !== ma) return mb - ma; // newest tour date first
    return String(b.created_at || "").localeCompare(String(a.created_at || ""));
  });

  const filteredProfiles = profiles.filter((p) =>
    p.name !== "Unknown" && (
    !query || fuzzyMatch(`${p.name} ${[p.city, p.state, p.country].filter(Boolean).join(" ")}`, query))
  );
  const unknownProfile = profiles.find((p) => p.name === "Unknown");

  // When not searching, surface the top ~10 most-repeat guests (2+ tours, Unknown excluded)
  // first, then the rest alphabetically. While searching, just show the filtered A–Z list.
  const topRepeat = query ? [] : filteredProfiles
    .filter((p) => (p.lifetime_tours || 0) >= 2)
    .sort((a, b) => (b.lifetime_tours || 0) - (a.lifetime_tours || 0) || String(a.name).localeCompare(String(b.name)))
    .slice(0, 10);
  const topNames = new Set(topRepeat.map((p) => p.name));
  const alphaProfiles = [...filteredProfiles]
    .filter((p) => query || !topNames.has(p.name))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));

  return (
    <div className="min-h-screen lg:pl-64">
      <SideNav tab={tab} onNavigate={changeTab} pendingCount={pendingCount} homeBadge={guests.filter((g) => g.booking_status === "Past (Update)").length} />
      <header className="sticky top-0 z-30 border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-xl">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(/logo.jpg)" }}
        />
        <div className="absolute inset-0 plaid-veil" />
        <div className="relative mx-auto flex max-w-7xl items-center justify-between px-5 py-2.5">
          <BrandLockup showTagline onClick={() => changeTab("home")} testid="home-logo-btn" />
          <div className="flex items-center gap-2 lg:hidden">
            <GlobalSearch variant="icon" />
            <HamburgerMenu activeTab={tab} onNavigate={changeTab} pendingCount={pendingCount} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-6 px-5 py-6 pb-28">
        <RetireBanner onShowExport={showExport} />
        <RetireDialog onShowExport={showExport} />
        <NotificationNudge />
        <PullToRefresh onRefresh={refreshForTab} enabled={["home", "tours", "calendar"].includes(tab)}>
        {tab === "home" && <HomeView tours={guests} loaded={guestsLoaded} onUpload={handleSmartUpload} onOpenCalendarDate={openCalendarDate} onRefresh={refresh} />}

        {tab === "settings" && <Settings onDataCleared={refresh} focusExport={focusExport} />}

        {tab === "admin" && user?.role === "admin" && (
          <AdminPage onChanged={() => api.adminPendingCount().then((d) => setPendingCount(d.count || 0)).catch(() => {})} />
        )}

        {tab === "callouts" && <CallOutsTab />}

        {tab === "insights" && (guests.length === 0
          ? (guestsLoaded ? <EmptyState variant="insights" testid="insights-empty" /> : null)
          : <InsightsView tours={guests} />)}

        {tab === "calendar" && <MonthCalendar tours={guests} onToursChanged={refresh} focusDate={calendarFocus} onConvert={convertToScheduled} />}

        {tab === "future" && <FutureRequests guests={guests} onConvert={convertToScheduled} onChanged={refresh} />}

        {(tab === "tours" || tab === "guests") && (
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-display text-xl font-800 tracking-tight text-zinc-50">
                {tab === "tours" ? (showPossible ? "Possible Tours" : "Tours") : "Guests"}
                <span className="ml-2 text-sm font-400 text-zinc-600">({tab === "tours" ? filtered.length : filteredProfiles.length})</span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {tab === "tours" && !showPossible && (
                  <Select value={dateRange} onValueChange={setDateRange}>
                    <SelectTrigger className="w-[150px] border-zinc-800 bg-zinc-900 focus:ring-[#E0575B]/70" data-testid="date-range-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-950">
                      <SelectItem value="all" data-testid="date-range-all">All dates</SelectItem>
                      <SelectItem value="last7" data-testid="date-range-last7">Last 7 days</SelectItem>
                      <SelectItem value="last30" data-testid="date-range-last30">Last 30 days</SelectItem>
                      <SelectItem value="week" data-testid="date-range-week">This week</SelectItem>
                      <SelectItem value="month" data-testid="date-range-month">This month</SelectItem>
                      <SelectItem value="year" data-testid="date-range-year">This year</SelectItem>
                      <SelectItem value="lastyear" data-testid="date-range-lastyear">Last year</SelectItem>
                      <SelectItem value="custom" data-testid="date-range-custom">Custom range…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {tab === "tours" && !showPossible && (
                  <button
                    onClick={togglePin}
                    title={isPinned ? "Pinned as default — click to clear" : "Pin this filter as your default view"}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors ${isPinned ? "border-[#9B2226]/50 bg-[#9B2226]/10 text-[#E0575B]" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-zinc-200"}`}
                    data-testid="pin-view-btn"
                  >
                    <Star className={`h-4 w-4 ${isPinned ? "fill-[#E0575B] text-[#E0575B]" : ""}`} />
                    {isPinned ? "Pinned" : "Pin"}
                  </button>
                )}
                {tab === "tours" && !showPossible && dateRange === "custom" && (
                  <div className="flex items-center gap-1.5" data-testid="custom-range-inputs">
                    <SingleDatePicker value={customFrom} onChange={setCustomFrom} testid="custom-from-input" placeholder="From" className="w-[150px]" />
                    <span className="text-xs text-zinc-400">to</span>
                    <SingleDatePicker value={customTo} onChange={setCustomTo} testid="custom-to-input" placeholder="To" className="w-[150px]" />
                  </div>
                )}
                <div className="relative w-full max-w-xs">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={tab === "tours" ? "Search tour, guest, type…" : "Search guest, location…"}
                    className="border-zinc-800 bg-zinc-900 pl-9 focus-visible:ring-[#E0575B]/70"
                    data-testid="search-input"
                  />
                </div>
              </div>
            </div>
            {tab === "tours" && showPossible && !possTipDismissed && (
              <div className="relative mb-4 flex items-start gap-3 rounded-2xl border border-[#9B2226]/45 bg-[#9B2226]/12 p-4" data-testid="possible-tours-tip">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9B2226]/20 text-[#E0575B]"><Info className="h-5 w-5" /></div>
                <div className="min-w-0 pr-6">
                  <p className="font-display text-sm font-800 tracking-tight text-zinc-50">What's a Possible Tour?</p>
                  <p className="mt-0.5 text-xs text-zinc-300">Your rebooking wishlist. When a past guest tells you they'd like to tour with you again — or hasn't booked a firm date yet — mark them <span className="text-[#E0575B]">Possible</span> so you don't lose track. When they lock in a date, tap the <span className="text-[#E0575B]">convert</span> action to move them into your real schedule.</p>
                </div>
                <button onClick={dismissPossTip} aria-label="Dismiss" data-testid="possible-tip-dismiss" className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-500/10 hover:text-zinc-300">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {tab === "tours"
              ? (guestsLoaded && guests.length === 0 && !query
                  ? <FirstTimeTip storageKey="sd_tours_tip" title="First time here? Add your first tour" body="Upload a tour-sheet screenshot and Stage Door auto-fills the guest, party size, dates and more — or add one by hand." onUpload={handleSmartUpload} uploadLabel="Upload a tour sheet" onAdd={() => navigate("/guest/new")} addLabel="Add by hand" testid="tours-first-tip" />
                  : <GuestTable guests={filtered} onDelete={setDeleteTarget} onConvert={showPossible ? convertToScheduled : undefined} />)
              : guestsLoaded && guests.length === 0 && !query
                ? <EmptyState variant="guests" onAction={() => navigate("/guest/new")} actionLabel="Add your first tour" testid="guests-empty" />
                : <>
                    {!query && <GuestFacts tours={guests} profiles={profiles} />}
                    <GuestsTable profiles={alphaProfiles} topRepeat={topRepeat} unknown={query ? null : unknownProfile} />
                  </>}
          </div>
        )}
        </PullToRefresh>
      </main>

      <div className="lg:hidden">
        <BottomNav value={tab} onChange={changeTab} onLongPress={handleNavLongPress} homeBadge={guests.filter((g) => g.booking_status === "Past (Update)").length} />
      </div>

      {["home", "tours", "calendar"].includes(tab) && (
        <QuickAddFab
          onAddTour={() => navigate("/guest/new")}
          onAddShift={onFabAddShift}
          onImport={() => fabFileRef.current?.click()}
          onAddTimeOff={() => setTimeOffOpen(true)}
        />
      )}
      <CallOutDialog open={timeOffOpen} onOpenChange={setTimeOffOpen} onSaved={() => {}} />
      <ScheduleReviewDialog
        open={schedReview.open}
        onOpenChange={(o) => setSchedReview((s) => ({ ...s, open: o }))}
        changes={schedReview.changes}
        mode="preview"
        busy={schedBusy}
        onApply={applyPendingSchedule}
      />
      <input
        ref={fabFileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        data-testid="fab-import-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleSmartUpload(f); }}
      />


      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        initialFields={previewFields}
        initialProfile={previewProfile}
        returnMatch={previewReturn}
        onSave={handleSave}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="lg-glass-strong border-0 rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this tour?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes {deleteTarget ? tourTitle(deleteTarget) : "this record"} from your database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 bg-transparent" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
